import React, { useEffect } from "react";
import {
  Clock,
  CheckCircle,
  Wallet,
  ArrowRight,
  RefreshCw,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../../components/ui/card";
import { useAuth } from "../../hooks/useAuth";
import { useInteractionTracking } from "../../hooks/useAnalytics";
import { useReferrer } from "../../hooks/useReferrer";
import { OMIGButton, OMIGCard, OMIGHeader } from "./OMIGScreenWrapper";

const OMIGWelcomeScreen = ({
  userData,
  onComplete,
  onFullApp,
  onReset,
  isReturningUser,
  partnerName,
}) => {
  const { trackButtonClick } = useInteractionTracking();
  const { displayConfig, isLoading: referrerLoading } = useReferrer();
  partnerName = referrerLoading ? undefined : displayConfig.partnerName;
  //console.log('WelcomeScreen displayConfig:', displayConfig);
  

  // Journey steps with updated copy
  const steps = [
    {
      icon: <CheckCircle className="w-6 h-6 text-crimsonGlory" />,
      title: "See your current rewards balance and earning activity",
      description: "",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-crimsonGlory" />,
      title: "Claim and choose rewards that fit your needs",
      description: "",
    },
    {
      icon: <CheckCircle className="w-6 h-6 text-crimsonGlory" />,
      title: "Track your reward history and upcoming opportunities",
      description: "",
    },
  ];

  // Check if user has existing progress
  const hasSurveyData = sessionStorage.getItem("surveyData");
  const hasUserData = sessionStorage.getItem("userData");
  const hasProgress = hasSurveyData || hasUserData;
  const surveyData = hasSurveyData ? JSON.parse(hasSurveyData) : {};
  const isCompletedSurvey = Object.keys(surveyData).length >= 3;

  return (
    <div className="space-y-8 p-4 pt-5 pb-6">
      {/* Welcome Header with updated copy */}
      <OMIGHeader
        title={"Welcome to The Ohio Mutual Rewards Center"}
        subtitle={
          "Sign in to view what you've earned, claim your rewards, and track your progress."
        }
      >
        <img className="" src="card.png" alt="Welcome image" />
      </OMIGHeader>

      {/* Time Commitment */}
      {/* {!isReturningUser ? (
        <OMIGCard variant="primary">
          <div className="flex items-center gap-3">
            <Clock className="min-w-[25px] min-h-[25px]" />
            <p className="text-base text-darkGrey">
              Takes less than 2 minutes to complete
            </p>
          </div>
        </OMIGCard>
      ) : (
        <div></div>
      )} */}

      {/* {!isReturningUser ? ( */}
      <OMIGCard className="bg-white border-none rounded-[20px] !mt-0">
        <div>
          <h2 className="text-base font-bold mb-4">Here's what to expect:</h2>
          <div className="space-y-4">
            {steps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="mt-[6px] min-w-[10px] min-h-[10px] bg-crimsonGlory/50 rounded-full"></span>
                <div>
                  <h3 className="font-base text-darkGrey">{step.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </OMIGCard>
      {/* ) : (
        <div></div>
      )} */}

      {/* CTAs */}
      <div className="space-y-3">
        <OMIGButton
          onClick={() => {
            trackButtonClick("omig_welcome_continue", {
              is_returning_user: isReturningUser,
              partner_name: partnerName,
              button_text: isReturningUser
                ? "Continue to Personal Information"
                : "Continue",
            });
            onComplete();
          }}
          className="w-full"
        >
          {isReturningUser ? "Continue to Personal Information" : "Continue"}

          <ArrowRight className="w-4 h-4" />
        </OMIGButton>

        {/* Only show skip to verification for new users */}
        {!isReturningUser && isCompletedSurvey && !hasUserData && (
          <OMIGButton
            onClick={() => {
              trackButtonClick("welcome_skip_to_verification", {
                has_survey_data: isCompletedSurvey,
                has_user_data: !!hasUserData,
              });
              // This will set a flag in session storage that App.js can use to skip to verification
              sessionStorage.setItem("skipToVerification", "true");
              onComplete();
            }}
            className="w-full"
          >
            Skip to Verification
          </OMIGButton>
        )}

        {/* Show reset button if there's existing progress */}
        {/* <button
          onClick={onReset}
          className="w-full flex items-center justify-center gap-2 text-gray-600 hover:text-gray-900 text-sm transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Start Over
        </button> */}

        <div className="text-center text-sm text-gray-600">
          By continuing, you agree to Ownli's{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011275958-Terms-of-Service"
            target="_blank"
            rel="noopener noreferrer"
            className="text-crimsonGlory hover:text-crimsonGlory/80 underline inline-flex items-center"
          >
            Terms of Service
          </a>{" "}
          and{" "}
          <a
            href="https://support.ownli.co/hc/en-us/articles/360011276138-Ownli-Privacy-Policy"
            target="_blank"
            rel="noopener noreferrer"
            className="text-crimsonGlory hover:text-crimsonGlory/80 underline inline-flex items-center"
          >
            Privacy Policy
          </a>
          .
        </div>
      </div>

      {/* Return User Section (conditionally rendered) */}
      {userData?.returnUser && (
        <Card className="bg-gray-50 border-gray-100">
          <CardContent className="pt-6">
            <p className="text-sm text-gray-600 text-center">
              Welcome back! Continue where you left off or start fresh.
            </p>
            <div className="mt-3 flex justify-center gap-2">
              <button
                onClick={onComplete}
                className="text-blue-600 text-sm hover:text-blue-700"
              >
                Continue
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={onReset}
                className="text-blue-600 text-sm hover:text-blue-700"
              >
                Start Over
              </button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OMIGWelcomeScreen;
